import { usePermissions } from "@/rbac/usePermissions";
import { useAppSelector } from "@/stores/hook";
import { selectAccessConfig } from "@/rbac/rbacSlice";
import { managedTrackIdsOf } from "@/features/leads/components/assign-lead/trackAssign";

/**
 * Lead / child / branch assignation gates from `global.assignation_management`.
 *
 * The role wizard now exposes six explicit toggles split by direction:
 *   outbound_lead   — this user can hand leads off to others
 *   outbound_child  — this user can assign child / deal records
 *   outbound_branch — this user can transfer leads between branches
 *   inbound_lead    — this user is eligible to RECEIVE assigned leads
 *   inbound_child   — this user is eligible to RECEIVE child records
 *   inbound_branch  — this user is eligible to RECEIVE branch transfers
 *
 * OUTBOUND drives UI on the actor's side — does this user even see the
 * "Assign" CTA? INBOUND drives UI on the receiver's side — should this user
 * appear in someone else's assignee picker? For the receiver-side check use
 * `roleAcceptsAssignment(role, kind)` below, which inspects the candidate
 * role's stored access_config rather than the current user's permissions.
 *
 * Legacy roles (pre-wizard) only stored three keys: `lead_assignation`,
 * `child_assignation`, `branch_assignation`. Those map onto OUTBOUND only —
 * legacy never modeled inbound. We fall back to them so old roles keep
 * working until the next save flushes the new shape.
 */
export function useAssignationPermissions() {
  const { canGlobal, bypassesRbacChecks } = usePermissions();
  const accessConfig = useAppSelector(selectAccessConfig);

  const check = (newKey: string, legacyKey?: string): boolean => {
    if (bypassesRbacChecks) return true;
    if (canGlobal("assignation_management", newKey)) return true;
    if (legacyKey && canGlobal("assignation_management", legacyKey)) return true;
    return false;
  };

  const canOutboundLead = check("outbound_lead", "lead_assignation");
  const canOutboundChild = check("outbound_child", "child_assignation");
  const canOutboundBranch = check("outbound_branch", "branch_assignation");
  // "Transfer to peers" — hand a lead/deal you HOLD to a same-role peer
  // (self-revoke handover). No legacy key; pure opt-in. This is NOT general
  // assign rights — peer mode only shows same-role users and the server
  // enforces actor-holds-the-slot + same-role.
  const canOutboundPeer = check("outbound_peer");

  // Inbound is NOT mirrored from the legacy three keys — legacy roles never
  // distinguished direction. Fall back to `true` only when the role is fully
  // legacy (no new outbound keys at all) so existing users remain receivable.
  const hasAnyNewKey =
    canGlobal("assignation_management", "outbound_lead") ||
    canGlobal("assignation_management", "outbound_child") ||
    canGlobal("assignation_management", "outbound_branch") ||
    canGlobal("assignation_management", "inbound_lead") ||
    canGlobal("assignation_management", "inbound_child") ||
    canGlobal("assignation_management", "inbound_branch");

  const inbound = (key: string): boolean => {
    if (bypassesRbacChecks) return true;
    if (canGlobal("assignation_management", key)) return true;
    return !hasAnyNewKey; // legacy role → assume receivable
  };

  const canInboundLead = inbound("inbound_lead");
  const canInboundChild = inbound("inbound_child");
  const canInboundBranch = inbound("inbound_branch");

  const canUseAssignFlow = canOutboundLead || canOutboundBranch;

  // TRACK HEAD — Manage on at least one track. May hand that track to its
  // Own members from the Assign popup even with every Outbound switch off
  // (track members are separate from lead assignees). Deliberately NOT folded
  // into `canUseAssignFlow`: that flag also unlocks un-assigning leads and
  // branch transfers, which a track head must not gain.
  const canAssignTracks =
    !bypassesRbacChecks && managedTrackIdsOf(accessConfig).length > 0;

  return {
    // New, direction-aware API (preferred):
    canOutboundLead,
    canOutboundChild,
    canOutboundBranch,
    canOutboundPeer,
    canInboundLead,
    canInboundChild,
    canInboundBranch,

    // Legacy aliases — kept so existing call-sites don't have to change in one
    // sweep. They map to the OUTBOUND counterparts because that's what the
    // old keys were really gating (the actor's ability to dispatch).
    canLeadAssignation: canOutboundLead,
    canBranchAssignation: canOutboundBranch,

    canUseAssignFlow,
    canAssignTracks,
  };
}

/**
 * Receiver-side gate. Reads a role's stored `access_config` to decide whether
 * users in that role are allowed to RECEIVE the given kind of assignment.
 *
 * This is the inbound mirror of `useAssignationPermissions` — `useAssignationPermissions`
 * answers "can the current user dispatch?", `roleAcceptsAssignment` answers
 * "is this candidate eligible to be picked?". It deliberately does NOT honor
 * the actor's `bypassesRbacChecks`: a super-admin should still respect a
 * role's "do not assign me" choice, otherwise the toggle is meaningless.
 *
 * Legacy roles (no inbound keys at all) default to accepting — the legacy
 * shape never modeled a refusal, and breaking those rows on read would
 * silently empty out assignee pickers across the app.
 */
export function roleAcceptsAssignment(
  role: unknown,
  kind: "lead" | "child" | "branch"
): boolean {
  if (!role || typeof role !== "object") return true;

  // Pipeline rows arrive as `{ permissions: [{ access_config: {...} }] }`.
  // We only look at the first permission row — multi-permission roles are
  // not a thing in this codebase yet, but if they ever become one we should
  // OR across them (more permissive wins on the receive side).
  const perms = (role as { permissions?: unknown }).permissions;
  const perm = Array.isArray(perms) ? perms[0] : perms;
  const accessConfig = (perm as { access_config?: unknown } | undefined)
    ?.access_config as
    | { global?: Record<string, unknown> }
    | undefined;
  const am = accessConfig?.global?.assignation_management as
    | Record<string, string>
    | undefined;
  if (!am || typeof am !== "object") return true;

  const newKey =
    kind === "lead"
      ? "inbound_lead"
      : kind === "child"
        ? "inbound_child"
        : "inbound_branch";
  const legacyKey =
    kind === "lead"
      ? "lead_assignation"
      : kind === "child"
        ? "child_assignation"
        : "branch_assignation";

  // Explicit answer on the new key wins.
  if (am[newKey] === "allow") return true;
  if (am[newKey] === "deny") return false;

  // Pre-wizard role: no inbound concept stored. Treat as accepting unless
  // even the legacy outbound mirror is an explicit deny (paranoid fallback).
  if (am[legacyKey] === "deny") return false;
  return true;
}

/**
 * Receiver-side gate for BRANCHLESS leads — the role wizard's "Inbound —
 * receive branchless leads" toggle. STRICT opt-in, unlike
 * `roleAcceptsAssignment`: a missing key means NO (pre-feature roles keep
 * today's "assign a branch first" behaviour). Mirrors the server's
 * `roleAllowsBranchlessAssignment`.
 */
export function roleAcceptsBranchless(role: unknown): boolean {
  if (!role || typeof role !== "object") return false;
  const perms = (role as { permissions?: unknown }).permissions;
  const perm = Array.isArray(perms) ? perms[0] : perms;
  const accessConfig = (perm as { access_config?: unknown } | undefined)
    ?.access_config as { global?: Record<string, unknown> } | undefined;
  const am = accessConfig?.global?.assignation_management as
    | Record<string, string>
    | undefined;
  return am?.inbound_branchless === "allow";
}

/**
 * "Lead user / Deal user" — WHICH LEVEL a role's users are assigned at (Edit
 * Role → Assignation → Assigned as). Stored at
 * `global.assignation_management.lead_user` / `.deal_user`; a missing key is
 * ON, so every role saved before the toggles existed holds at both levels.
 * Both off is not a state the wizard can save and reads as both on. Mirrors
 * the server's `readHolderLevels` (core/assignment/holder-level.ts), which
 * enforces it on every assignment write (drawer, bulk, automations).
 */
export interface RoleHolderLevels {
  lead: boolean;
  deal: boolean;
}

export function holderLevelsFromAssignation(am: unknown): RoleHolderLevels {
  if (!am || typeof am !== "object") return { lead: true, deal: true };
  const rec = am as Record<string, unknown>;
  const lead = rec.lead_user !== "deny";
  const deal = rec.deal_user !== "deny";
  if (!lead && !deal) return { lead: true, deal: true };
  return { lead, deal };
}

/** {@link holderLevelsFromAssignation} for a role-pipeline row. */
export function roleHolderLevels(role: unknown): RoleHolderLevels {
  if (!role || typeof role !== "object") return { lead: true, deal: true };
  const perms = (role as { permissions?: unknown }).permissions;
  const perm = Array.isArray(perms) ? perms[0] : perms;
  const accessConfig = (perm as { access_config?: unknown } | undefined)
    ?.access_config as { global?: Record<string, unknown> } | undefined;
  return holderLevelsFromAssignation(
    accessConfig?.global?.assignation_management
  );
}

/**
 * The level an assignment to this role actually lands on when the picker was
 * opened for `kind`: a deal-user role picked on a lead lands on its deals, a
 * lead-user role picked on a deal lands on the lead. Pickers check the
 * role's inbound toggle against THIS level, not the slot they were opened on.
 */
export function effectiveAssignKind(
  role: unknown,
  kind: "lead" | "child"
): "lead" | "child" {
  const levels = roleHolderLevels(role);
  if (kind === "lead" && !levels.lead) return "child";
  if (kind === "child" && !levels.deal) return "lead";
  return kind;
}

/** Short badge for a single-level role; null for the default (both). */
export function holderLevelBadge(role: unknown): string | null {
  const levels = roleHolderLevels(role);
  if (levels.lead && levels.deal) return null;
  return levels.deal ? "Deal user" : "Lead user";
}
